import React, { useState, useEffect, useContext } from "react";
import MobileNavDrawer from "../components/MobileNavDrawer";
import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useParams, useLocation } from "react-router-dom";
import ResponsiveTopBar from "../components/ResponsiveTopBar";
import LeftSidebarPosts from "../components/LeftSidebarPosts";
import PostInPostPage from "../components/PostInPostPage";
import StockInfo from "../components/StockInfo";
import { SymbolInfo, FundamentalData, CompanyProfile } from "react-tradingview-embed";
import MobileWidgetDrawer from "../components/MobileWidgetDrawer";
import RightSidebar from "../components/RightSidebar";
import { useTheme } from "../components/ThemeContext";
import { DeviceContext } from "../context/DeviceContext";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import BarChartIcon from "@mui/icons-material/BarChart";
import BusinessIcon from "@mui/icons-material/Business";

const SIDEBAR_LEFT_WIDTH = 264;
const SIDEBAR_RIGHT_WIDTH = 420;
const TOPBAR_HEIGHT = 64;

function PostPage() {
  const { postId } = useParams();
  const location = useLocation();
  const { theme } = useTheme();
  const { isMobile } = useContext(DeviceContext);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [post, setPost] = useState(location.state?.post || null);
  const [comments, setComments] = useState(null);

  // ── Data fetching ────────────────────────────────────────────────────────────
  useEffect(() => {
    setPost(null);
    const fetchPost = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_SC_API_BASE_URL}/api/posts/byId?postId=${postId}`
        );
        const data = await res.json();
        setPost(data);
      } catch (err) {
        console.error("Error fetching post:", err);
      }
    };
    fetchPost();
  }, [postId]);

  useEffect(() => {
    setComments(null);
    const fetchComments = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_SC_API_BASE_URL}/api/comments/post/${postId}/nested`
        );
        const data = await res.json();
        setComments(data);
      } catch (err) {
        console.error("Error fetching comments:", err);
      }
    };
    fetchComments();
  }, [postId]);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const updatePosts = (id, upvotes, downvotes) => {
    setPost((prev) =>
      prev?.postId === id ? { ...prev, upvotes, downvotes } : prev
    );
  };

  const updateComments = (newComments) => {
    setComments(newComments);
  };

  // ── Stock name resolution ─────────────────────────────────────────────────────
  const stockName = post
    ? (() => {
        const tags = post.tags || [];
        if (post.communityName?.includes(":")) return post.communityName;
        const tickerTag = tags.find((t) => t.includes(":"));
        return tickerTag || null;
      })()
    : null;

  // ── Mobile widget list — one icon per StockInfo sub-widget ──────────────────
  const ticker = stockName ? (stockName.split(":")[1] || stockName) : null;
  const mobileWidgets = stockName
    ? [
        {
          id: "symbolinfo",
          label: ticker,
          icon: ShowChartIcon,
          activeColor: "#05df72",
          activeBg: "rgba(0,201,80,0.12)",
          activeBorder: "rgba(0,201,80,0.35)",
          render: () => (
            <Box sx={{ p: 1 }}>
              <SymbolInfo widgetProps={{ symbol: stockName, width: "100%", height: 400, locale: "en" }} />
            </Box>
          ),
        },
        {
          id: "fundamentals",
          label: "Fundamentals",
          icon: BarChartIcon,
          activeColor: "#38bdf8",
          activeBg: "rgba(56,189,248,0.12)",
          activeBorder: "rgba(56,189,248,0.35)",
          render: () => (
            <Box sx={{ p: 1 }}>
              <FundamentalData widgetProps={{ symbol: stockName, width: "100%", height: 500, colorTheme: "dark", isTransparent: false, displayMode: "regular", locale: "en" }} />
            </Box>
          ),
        },
        {
          id: "company",
          label: "Company",
          icon: BusinessIcon,
          activeColor: "#a78bfa",
          activeBg: "rgba(167,139,250,0.12)",
          activeBorder: "rgba(167,139,250,0.35)",
          render: () => (
            <Box sx={{ p: 1 }}>
              <CompanyProfile widgetProps={{ symbol: stockName, width: "100%", height: 500, colorTheme: "dark", isTransparent: false, locale: "en" }} />
            </Box>
          ),
        },
      ]
    : [];

  // ── Center content ────────────────────────────────────────────────────────────
  const centerContent = post ? (
    <PostInPostPage
      key={postId}
      post={post}
      comments={comments}
      updateComments={updateComments}
      updatePosts={updatePosts}
    />
  ) : (
    <Box sx={{ p: 4, color: "#6a7282", fontSize: "0.875rem" }}>Loading…</Box>
  );

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <>
      <ResponsiveTopBar isMobile={isMobile} onMobileMenuToggle={() => setMobileMenuOpen((prev) => !prev)} />
      <MobileNavDrawer open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />

      {isMobile ? (
        // ── MOBILE: flat layout, no tabs, no bottom nav ───────────────────────
        <Box
          sx={{
            mt: `${TOPBAR_HEIGHT}px`,
            minHeight: "100vh",
            backgroundColor: theme.palette.background.default,
            px: 2,
            py: 2,
            pb: "6rem", // clearance for the FAB
          }}
        >
          {centerContent}
          {/* Widget FAB — always visible; passes stock widgets when available */}
          <MobileWidgetDrawer widgets={mobileWidgets.length > 0 ? mobileWidgets : undefined} />
        </Box>
      ) : (
        // ── DESKTOP: three-column fixed layout ───────────────────────────────
        <Box
          sx={{
            display: "flex",
            minHeight: "100vh",
            backgroundColor: theme.palette.background.default,
            pt: `${TOPBAR_HEIGHT}px`,
          }}
        >
          {/* Left sidebar — fixed, hidden below lg */}
          <Box
            sx={{
              display: { xs: "none", md: "none", lg: "block" },
              position: "fixed",
              top: TOPBAR_HEIGHT,
              left: 0,
              bottom: 0,
              width: SIDEBAR_LEFT_WIDTH,
              backgroundColor: theme.palette.background.paper,
              borderRight: `1px solid ${theme.palette.divider}`,
              overflowY: "auto",
              overflowX: "hidden",
              "&::-webkit-scrollbar": { width: 4 },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: alpha(theme.palette.primary.main, 0.2),
                borderRadius: 2,
              },
              zIndex: 100,
            }}
          >
            <LeftSidebarPosts />
          </Box>

          {/* Center content — scrollable */}
          <Box
            sx={{
              ml: { xs: 0, lg: `${SIDEBAR_LEFT_WIDTH}px` },
              mr: { xs: 0, lg: `${SIDEBAR_RIGHT_WIDTH}px` },
              flex: 1,
              minWidth: 0,
              py: 4,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: 800,
                px: { md: 3, lg: 4 },
              }}
            >
              {centerContent}
            </Box>
          </Box>

          {/* Right sidebar — fixed, always visible */}
          <Box
            sx={{
              display: { xs: "none", md: "none", lg: "block" },
              position: "fixed",
              top: TOPBAR_HEIGHT,
              right: 0,
              bottom: 0,
              width: SIDEBAR_RIGHT_WIDTH,
              backgroundColor: theme.palette.background.paper,
              borderLeft: `1px solid ${theme.palette.divider}`,
              overflowY: "auto",
              overflowX: "hidden",
              "&::-webkit-scrollbar": { width: 4 },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: alpha(theme.palette.primary.main, 0.2),
                borderRadius: 2,
              },
              zIndex: 100,
            }}
          >
            {stockName ? (
              <Box sx={{ p: 2 }}>
                <StockInfo stockName={stockName} />
              </Box>
            ) : (
              <RightSidebar />
            )}
          </Box>
        </Box>
      )}
    </>
  );
}

export default PostPage;
